import { Button } from "@solomon-apps/shared/src/components/ui/button";
import { Heading } from "@solomon-apps/shared/src/components/ui/heading";
import { Text } from "@solomon-apps/shared/src/components/ui/text";
import { easings } from "@solomon-apps/shared/src/utils/easings";
import { motion } from "motion/react";
import type { ReactElement } from "react";

import { useOnboardingApplicationContextOrNull } from "../OnboardingApplicationContext";
import { useOnboardingSubmit } from "../onboardingSubmitContext";
import {
    buildReviewSections,
    type ReviewSection,
    submittedReviewCopy
} from "../reviewSummary";
import type { SubsectionId } from "../types";
import type { WalletEntry } from "../walletEntries";

/**
 * Review and submit — the whole application read as one document, and the
 * one place it is filed from.
 *
 * Everything above is entered a section at a time, often over days. Nothing
 * showed the applicant the result as a single thing before they committed
 * to it, and Submit sat in the page footer, far from anything it described.
 * After submission the same list stays as the read-only record of what was
 * sent.
 */
export function ReviewAndSubmitPanel({
    jumpToCard,
    walletEntries = []
}: {
    jumpToCard: (id: SubsectionId) => void;
    /** The hub's verified-wallet list; the wallet step carries no payload. */
    walletEntries?: readonly WalletEntry[];
}): ReactElement {
    const context = useOnboardingApplicationContextOrNull();
    const submit = useOnboardingSubmit();
    const sections = buildReviewSections(context?.steps ?? [], walletEntries);
    const status = context?.application.status;
    const submitted = status !== undefined && status !== "draft";

    return (
        <motion.div
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2, ease: easings.outCubic }}
        >
            <Heading
                title="Review and submit"
                className="pb-4 border-b border-line-base"
            />

            <Text
                variant="small"
                className="block leading-relaxed pt-4 text-foreground-muted"
            >
                {submitted
                    ? submittedReviewCopy(status)
                    : "Check this reads the way you intend, then submit. Solomon reviews the application and sends the agreements for signature once it is approved."}
            </Text>

            {sections.length === 0 ? (
                <Text
                    variant="small"
                    className="block leading-relaxed pt-8 text-foreground-muted"
                >
                    Nothing saved yet. Complete the sections above and they will
                    appear here.
                </Text>
            ) : (
                <div className="pt-4">
                    {sections.map((section) => (
                        <ReviewSectionBlock
                            key={section.stepName}
                            section={section}
                            editable={!submitted}
                            onEdit={() => jumpToCard(section.subsectionId)}
                        />
                    ))}
                </div>
            )}

            {!submitted && (
                <div className="pt-10">
                    <Button
                        variant="raised"
                        interaction="light-bar"
                        size="lg"
                        fullWidth
                        disabled={!submit?.submittable || !submit.submit}
                        onClick={() => submit?.submit?.()}
                    >
                        {submit?.submitting
                            ? "Submitting…"
                            : "Submit Application"}
                    </Button>
                    {submit && !submit.submittable && (
                        <Text
                            variant="small"
                            className="block leading-snug text-foreground-muted pt-3"
                        >
                            Complete every section above before submitting.
                        </Text>
                    )}
                    {submit?.error && (
                        <Text
                            variant="small"
                            className="block leading-snug text-error-base pt-3"
                        >
                            {submit.error}
                        </Text>
                    )}
                </div>
            )}
        </motion.div>
    );
}

/**
 * One section's saved values. Also used on a frozen applicant section, so a
 * submitted application shows its own record in place of the form it can no
 * longer accept — see `FrozenSectionPanel`.
 */
export function ReviewSectionBlock({
    section,
    editable,
    onEdit
}: {
    section: ReviewSection;
    editable: boolean;
    onEdit?: () => void;
}): ReactElement {
    return (
        <section className="py-4 border-b border-line-base last:border-b-0">
            <div className="flex items-baseline justify-between gap-4">
                <Heading title={section.title} titleVariant="h3" />
                {editable && onEdit && (
                    <Button
                        type="button"
                        variant="transparent"
                        interaction="underline"
                        size="fit"
                        onClick={onEdit}
                        className="text-foreground-base hover:text-accent-base"
                    >
                        <Text variant="label" render={<span />}>
                            Edit
                        </Text>
                    </Button>
                )}
            </div>
            {section.rows.length === 0 ? (
                <Text
                    variant="small"
                    className="block leading-snug pt-2 text-foreground-muted"
                >
                    Nothing saved yet.
                </Text>
            ) : (
                <dl className="pt-2 space-y-1.5">
                    {section.rows.map((row, index) => (
                        <div
                            // Two wallets may carry the same label.
                            key={`${index}-${row.label}`}
                            className="flex flex-wrap items-baseline gap-x-3"
                        >
                            <Text
                                variant="label"
                                render={<dt />}
                                className="text-foreground-muted min-w-40"
                            >
                                {row.label}
                            </Text>
                            <Text
                                variant="small"
                                render={<dd />}
                                className="leading-snug text-foreground-base"
                            >
                                {row.value}
                            </Text>
                        </div>
                    ))}
                </dl>
            )}
        </section>
    );
}
